/**
 * The sidebar: who is playing, and what has happened.
 *
 * Every player is a row — score, votes, what they hold, what they have on
 * display — and pointing at one lays their tableau on the table in place of
 * yours, because "what has Sam got?" is the question you ask before every turn
 * and it should not need a click. Clicking a row keeps it there until clicked
 * again. The log below is the events the server sent, one sentence each; see
 * `narrate.ts`.
 */
import { senateVotes, type GameEvent, type PlayerId, type View } from '@trajan/engine';
import { useEffect, useRef } from 'react';
import { groupCards } from '../cards/cards';
import { narrateAll } from './narrate';

export function Sidebar({
  view,
  seat,
  events,
  nameOf,
  connected,
  deciding,
  peek,
  pinned,
  onPeek,
  onPin,
}: {
  view: View;
  /** The viewer's own seat, or null for a spectator. */
  seat: PlayerId | null;
  events: readonly GameEvent[];
  nameOf: (seat: PlayerId) => string;
  /** Seats currently connected; absent means "not known", so nothing is shown. */
  connected?: ((seat: PlayerId) => boolean) | undefined;
  deciding: PlayerId | null;
  /** The seat whose tableau is laid on the table while pointed at, if any. */
  peek?: PlayerId | null;
  pinned?: PlayerId | null;
  onPeek?: ((seat: PlayerId | null) => void) | undefined;
  onPin?: ((seat: PlayerId | null) => void) | undefined;
}) {
  return (
    <aside className="sidebar">
      <ul className="players">
        {view.players.map((p) => {
          const display = groupCards(p.display);
          return (
            <li key={p.id}>
              <button
                type="button"
                className={
                  'player-row' +
                  (p.id === deciding ? ' player-row-turn' : '') +
                  (p.id === seat ? ' player-row-you' : '') +
                  (p.id === (peek ?? pinned) ? ' player-row-shown' : '')
                }
                // Pointer tracking is for mice only: on a touch screen the tap
                // would otherwise show the tableau and the click hide it again.
                onPointerEnter={(e) => e.pointerType === 'mouse' && onPeek?.(p.id)}
                onPointerLeave={(e) => e.pointerType === 'mouse' && onPeek?.(null)}
                onFocus={(e) => e.target.matches(':focus-visible') && onPeek?.(p.id)}
                onBlur={() => onPeek?.(null)}
                onClick={() => onPin?.(pinned === p.id ? null : p.id)}
                aria-pressed={pinned === p.id}
                title={
                  p.id === seat ? 'Your tableau' : `Show ${nameOf(p.id)}’s tableau on the table`
                }
              >
                <span className={`seat-dot seat-dot-${p.id}`} aria-hidden="true" />
                <span className="player-name">
                  {nameOf(p.id)}
                  {p.id === seat ? ' (you)' : ''}
                </span>
                <span className="player-vp">{p.vp}</span>
                <span className="player-holdings">
                  {senateVotes(p)} votes · {p.forumTiles.length} forum ·{' '}
                  {p.constructionTiles.length} built · {p.handCount} in hand
                  {p.id === deciding ? ' · to play' : ''}
                  {connected && !connected(p.id) ? ' · away' : ''}
                </span>
                {display.length > 0 && (
                  <span className="player-display">
                    on display:{' '}
                    {display.map((g) => (g.count > 1 ? `${g.count} ${g.type}` : g.type)).join(', ')}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <GameLog events={events} nameOf={nameOf} />
    </aside>
  );
}

/** Newest at the bottom, scrolled to as it grows — like a chat, because it is one. */
export function GameLog({
  events,
  nameOf,
}: {
  events: readonly GameEvent[];
  nameOf: (seat: PlayerId) => string;
}) {
  const lines = narrateAll(events, nameOf);
  const box = useRef<HTMLOListElement>(null);
  const stick = useRef(true);

  useEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  return (
    <section className="log" aria-label="Game log">
      <h4>Game log</h4>
      {lines.length === 0 && <p className="log-empty">Nothing has happened yet.</p>}
      <ol
        className="log-lines"
        ref={box}
        onScroll={(e) => {
          const el = e.currentTarget;
          // Only follow the newest line while the reader is already at the end.
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
      >
        {lines.map((line) => (
          <li
            key={line.id}
            className={
              'log-line' +
              (line.weight === 'loud' ? ' log-line-loud' : '') +
              (line.player !== null ? ` log-line-seat-${line.player}` : '')
            }
          >
            {line.text}
          </li>
        ))}
      </ol>
    </section>
  );
}
